export type GuestLessonProgress = {
  lessonId: string;
  completedLines: number[];
  completedChallengeLines: number[];
  practiceLineProgress: number[];
  challengeLineProgress: number[];
  challengeCompleted: boolean;
  updatedAt: number;
};

const GUEST_LESSON_PROGRESS_STORAGE_KEY = "chesshotel:lesson-progress:v1";

let memoryStore: Record<string, GuestLessonProgress> = {};
let persistent = true;
export const guestLessonsPersisted = () => persistent;

const isClient = () => typeof window !== "undefined";

const readGuestLessonProgressStore = (): Record<
  string,
  GuestLessonProgress
> => {
  if (!isClient()) {
    return {};
  }

  if (!persistent) return memoryStore;
  try {
    const rawValue = window.localStorage.getItem(
      GUEST_LESSON_PROGRESS_STORAGE_KEY,
    );

    if (!rawValue) {
      return {};
    }

    const parsedValue = JSON.parse(rawValue);
    if (
      !parsedValue ||
      typeof parsedValue !== "object" ||
      Array.isArray(parsedValue)
    ) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(parsedValue).filter(
        ([key, value]: [string, any]) =>
          value &&
          value.lessonId === key &&
          Array.isArray(value.completedLines) &&
          Array.isArray(value.completedChallengeLines) &&
          Number.isFinite(value.updatedAt),
      ),
    ) as Record<string, GuestLessonProgress>;
  } catch {
    try {
      window.localStorage.getItem(GUEST_LESSON_PROGRESS_STORAGE_KEY);
    } catch {
      persistent = false;
    }
    return memoryStore;
  }
};

const writeGuestLessonProgressStore = (
  store: Record<string, GuestLessonProgress>,
) => {
  if (!isClient()) {
    return;
  }

  memoryStore = store;
  try {
    window.localStorage.setItem(
      GUEST_LESSON_PROGRESS_STORAGE_KEY,
      JSON.stringify(store),
    );
    persistent = true;
  } catch {
    persistent = false;
  }
};

export const getGuestLessonProgress = (
  lessonId: string,
): GuestLessonProgress | null => {
  if (!lessonId) {
    return null;
  }

  const store = readGuestLessonProgressStore();
  return store[lessonId] ?? null;
};

export const getAllGuestLessonProgress = (): Record<
  string,
  GuestLessonProgress
> => {
  return readGuestLessonProgressStore();
};

export const saveGuestLessonProgress = (
  progress: Omit<GuestLessonProgress, "updatedAt">,
) => {
  if (!progress.lessonId) {
    return;
  }

  const store = readGuestLessonProgressStore();
  store[progress.lessonId] = {
    ...progress,
    updatedAt: Date.now(),
  };

  writeGuestLessonProgressStore(store);
};

/** Remove only the acknowledged snapshot; another tab may have saved more meanwhile. */
export function clearTransferredGuestLessons(snapshot: Record<string, GuestLessonProgress>) {
  const store = readGuestLessonProgressStore();
  for (const [id, progress] of Object.entries(snapshot)) {
    if (JSON.stringify(store[id]) === JSON.stringify(progress)) delete store[id];
  }
  writeGuestLessonProgressStore(store);
}
